import { useState, useEffect } from 'react';
import { getPortals, createPortal, deletePortal, fetchPortalOrders, scrapePortalDetails, confirmPortalImport, getExecutors } from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import { StyleNoPromptDialog } from '@/components/StyleNoPromptDialog';
import { ImagesUploadedTable } from '@/components/ImagesUploadedTable';
import { ClientSelect } from '@/lib/ClientSelect';
import { Globe, Plus, Trash2, Download, Loader2, Check, X, Sparkles, ChevronDown, ChevronRight, AlertTriangle, CheckSquare, Square } from 'lucide-react';
import {
  PIECE_TYPES, METAL_TYPES, METAL_COLOURS, METAL_KARATS, PRIORITY_LEVELS, DESIGNERS,
  STONE_SHAPES, SETTING_TYPES, FINDINGS_BY_PIECE_TYPE,
} from '@/utils/constants';
import { FINISHING_TYPES } from '@/utils/cad-sheet';

export default function PortalImportPage() {
  const [portals, setPortals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [portalForm, setPortalForm] = useState({ portal_name: 'StarGems', portal_url: 'https://custom.stargems.com', username: '', password: '', notes: '' });
  const [saving, setSaving] = useState(false);

  // Step 1: Order list
  const [step, setStep] = useState('portals'); // portals | list | details | importing
  const [fetching, setFetching] = useState(false);
  const [orderList, setOrderList] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [activePortalId, setActivePortalId] = useState(null);

  // Step 2: Detail scrape + preview
  const [scraping, setScraping] = useState(false);
  const [importSession, setImportSession] = useState(null);
  const [selectedForImport, setSelectedForImport] = useState(new Set());
  const [confirming, setConfirming] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [editingOrderIdx, setEditingOrderIdx] = useState(null);

  // Style-No prompt — collected for selected orders missing one, just before confirm
  const [styleNoPromptOpen, setStyleNoPromptOpen] = useState(false);
  const [styleNoPromptItems, setStyleNoPromptItems] = useState([]);
  // CAD due date prompt — fires after style_no is satisfied, mirrors the same
  // dialog so users can fill the missing due dates without bouncing back to the
  // preview rows one at a time.
  const [dueDatePromptOpen, setDueDatePromptOpen] = useState(false);
  const [dueDatePromptItems, setDueDatePromptItems] = useState([]);
  const [executors, setExecutors] = useState([]);

  useEffect(() => {
    getExecutors()
      .then(res => setExecutors(res.data || []))
      .catch(() => setExecutors([]));
  }, []);

  useEffect(() => { fetchPortals(); }, []);

  const fetchPortals = async () => {
    try { const res = await getPortals(); setPortals(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleAddPortal = async (e) => {
    e.preventDefault();
    if (!portalForm.portal_name || !portalForm.username || !portalForm.password) { toast.error('Fill all required fields'); return; }
    setSaving(true);
    try {
      await createPortal(portalForm);
      toast.success('Portal added');
      setShowAddForm(false);
      setPortalForm({ portal_name: 'StarGems', portal_url: 'https://custom.stargems.com', username: '', password: '', notes: '' });
      fetchPortals();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  // STEP 1: Fetch open orders. Sketch Orders are intentionally excluded — the
  // factory side does not handle them and the user does not want them showing
  // up in the import preview. Only CAD Requests come through here.
  const handleFetchOrders = async (portalId) => {
    setActivePortalId(portalId);
    setFetching(true);
    setOrderList([]);
    setSelectedOrders(new Set());
    try {
      const res = await fetchPortalOrders({ portal_id: portalId, tabs: ["cad_request"] });
      const orders = res.data.orders || [];
      setOrderList(orders);
      // Pre-select only the orders that have NOT already been imported into
      // KS-Tracker, so the user doesn't accidentally re-import duplicates.
      // Already-imported rows still appear in the list (greyed out + badged)
      // so the user can see what's been handled — they just start unchecked.
      const freshOrders = orders.filter(o => !o.already_imported).map(o => o.order_no);
      setSelectedOrders(new Set(freshOrders));
      setStep('list');
      const dupeCount = orders.length - freshOrders.length;
      toast.success(
        `Fetched ${res.data.count} CAD orders from SGI` +
        (dupeCount > 0 ? ` — ${dupeCount} already imported (unchecked)` : '')
      );
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to fetch orders'); }
    finally { setFetching(false); }
  };

  const toggleOrder = (orderNo) => {
    setSelectedOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderNo)) next.delete(orderNo); else next.add(orderNo);
      return next;
    });
  };

  // "Select All" intentionally skips already-imported orders. If the user
  // really wants to re-import one they can click that row manually — but the
  // bulk action should never silently queue a duplicate.
  const selectableOrders = orderList.filter(o => !o.already_imported);
  const selectAll = () => {
    const allSelectable = selectableOrders.length > 0 && selectedOrders.size >= selectableOrders.length;
    if (allSelectable) setSelectedOrders(new Set());
    else setSelectedOrders(new Set(selectableOrders.map(o => o.order_no)));
  };

  // STEP 2: Scrape details for selected orders
  const handleScrapeDetails = async () => {
    if (selectedOrders.size === 0) { toast.error('Select orders first'); return; }
    setScraping(true);
    try {
      const res = await scrapePortalDetails({
        portal_id: activePortalId,
        order_nos: Array.from(selectedOrders),
        // Pass full list-step rows so detail can preserve notes/order_by/comp_no/is_rush
        list_orders: orderList.filter(o => selectedOrders.has(o.order_no)),
      });
      setImportSession(res.data);
      setSelectedForImport(new Set(res.data.orders?.map((_, i) => i) || []));
      setStep('details');
      // Auto-open first order for review/editing
      if (res.data.orders?.length > 0) {
        setEditingOrderIdx(0);
        setExpandedOrder(0);
      }
      toast.success(`Scraped ${res.data.orders?.length} order details — review below`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Scrape failed'); }
    finally { setScraping(false); }
  };

  // STEP 3: Confirm import. Gate on missing style_no for every selected order —
  // style_no is mandatory for naming pieces, so prompt if any are blank.
  const handleConfirmImport = async () => {
    if (!importSession || selectedForImport.size === 0) return;
    const selectedOrderObjs = Array.from(selectedForImport)
      .map(idx => ({ idx, order: importSession.orders[idx] }))
      .filter(({ order }) => order && !order.error);

    // Style No is not compulsory anymore. Proceed straight to CAD due date check.
    promptForMissingDueDates(selectedOrderObjs);
  };

  const promptForMissingDueDates = (selectedOrderObjs) => {
    const list = selectedOrderObjs || Array.from(selectedForImport)
      .map(idx => ({ idx, order: importSession.orders[idx] }))
      .filter(({ order }) => order && !order.error);
    const missingDue = list.filter(({ order }) =>
      !(order.target_date || order.deadline || '').toString().trim()
    );
    if (missingDue.length > 0) {
      setDueDatePromptItems(missingDue.map(({ idx, order }) => ({
        key: String(idx),
        label: `Order #${order.order_no}${order.product_name ? ` — ${order.product_name.slice(0, 40)}` : ''}${order.style_no ? ` (${order.style_no})` : ''}`,
      })));
      setDueDatePromptOpen(true);
      return;
    }
    doConfirmImport(importSession.orders);
  };

  const handleDueDatePromptSave = (values) => {
    const updated = importSession.orders.map((order, idx) => {
      const key = String(idx);
      const v = values[key];
      if (!v) return order;
      // Keep target_date and deadline in lockstep so whichever field other
      // surfaces read (CAD summary edit reads `deadline`; filter sidebars
      // read `target_date`) both see the same value.
      return { ...order, target_date: v, deadline: v };
    });
    setImportSession(prev => ({ ...prev, orders: updated }));
    setDueDatePromptOpen(false);
    setDueDatePromptItems([]);
    doConfirmImport(updated);
  };

  const doConfirmImport = async (ordersToSend) => {
    setConfirming(true);
    try {
      const res = await confirmPortalImport({
        import_id: importSession.id,
        selected_indices: Array.from(selectedForImport),
        orders: ordersToSend,
      });
      const created = res.data.count || 0;
      const skipped = res.data.skipped || [];
      if (created > 0) toast.success(`Imported ${created} order${created === 1 ? '' : 's'} as CAD task${created === 1 ? '' : 's'}!`);
      // Surface duplicate-style_no (or other) failures inline — users need to
      // know exactly which orders need a unique style edit before re-import.
      if (skipped.length > 0) {
        for (const s of skipped) {
          toast.error(`Order ${s.order_no}: ${s.message}`, { duration: 8000 });
        }
      }
      if (created === 0 && skipped.length > 0) {
        // All orders failed — keep the user on the preview screen so they can
        // fix the conflicting style numbers without restarting the import.
        return;
      }
      setStep('portals');
      setImportSession(null);
      fetchPortals();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    finally { setConfirming(false); }
  };

  const handleStyleNoPromptSave = async (values) => {
    // Merge collected style_nos back into orders by index
    const updatedOrders = importSession.orders.map((order, idx) => {
      const key = String(idx);
      if (values[key]) return { ...order, style_no: values[key] };
      return order;
    });
    setImportSession({ ...importSession, orders: updatedOrders });
    setStyleNoPromptOpen(false);
    setStyleNoPromptItems([]);
    // After style_no is filled, chain into the due-date check against the
    // freshly-updated order objects (not the stale state we just setStateʼd).
    const selectedOrderObjs = Array.from(selectedForImport)
      .map(idx => ({ idx, order: updatedOrders[idx] }))
      .filter(({ order }) => order && !order.error);
    const missingDue = selectedOrderObjs.filter(({ order }) =>
      !(order.target_date || order.deadline || '').toString().trim()
    );
    if (missingDue.length > 0) {
      setDueDatePromptItems(missingDue.map(({ idx, order }) => ({
        key: String(idx),
        label: `Order #${order.order_no}${order.product_name ? ` — ${order.product_name.slice(0, 40)}` : ''}${order.style_no ? ` (${order.style_no})` : ''}`,
      })));
      setDueDatePromptOpen(true);
      return;
    }
    await doConfirmImport(updatedOrders);
  };

  return (
    <CollapsibleLayout>
      <div className="min-h-[calc(100vh-48px)] bg-slate-50">
        <header className="bg-slate-900 text-white px-6 py-3 sticky top-12 z-40">
          <div className="flex items-center justify-between max-w-[1400px] mx-auto">
            <div className="flex items-center gap-3">
              <Globe size={20} className="text-teal-400" />
              <div>
                <h1 className="text-lg font-semibold" data-testid="portal-page-title">SGI Order Import</h1>
                <p className="text-slate-400 text-xs">Import CAD orders from StarGems portal</p>
              </div>
            </div>
            {step !== 'portals' && (
              <Button variant="outline" size="sm" onClick={() => setStep('portals')} className="bg-transparent border-slate-600 text-white hover:bg-slate-800 h-7 text-xs">
                <X size={13} className="mr-1" /> Back to Portals
              </Button>
            )}
          </div>
        </header>

        <main className="max-w-[1400px] mx-auto p-6">

          {/* ===== PORTALS VIEW ===== */}
          {step === 'portals' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-base font-semibold">SGI Portal Connections</h2>
                <Button size="sm" onClick={() => setShowAddForm(!showAddForm)} className="bg-teal-600 hover:bg-teal-700" data-testid="add-portal-btn">
                  <Plus size={14} className="mr-1" /> Add Portal
                </Button>
              </div>

              {showAddForm && (
                <form onSubmit={handleAddPortal} className="p-4 bg-white rounded-lg border border-teal-200 space-y-3" data-testid="add-portal-form">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Portal Name *</Label><Input value={portalForm.portal_name} onChange={e => setPortalForm(p => ({ ...p, portal_name: e.target.value }))} className="h-8 text-sm mt-1" data-testid="portal-name-input" /></div>
                    <div><Label className="text-xs">Portal URL</Label><Input value={portalForm.portal_url} onChange={e => setPortalForm(p => ({ ...p, portal_url: e.target.value }))} className="h-8 text-sm mt-1" /></div>
                    <div><Label className="text-xs">SGI Username *</Label><Input value={portalForm.username} onChange={e => setPortalForm(p => ({ ...p, username: e.target.value }))} className="h-8 text-sm mt-1" data-testid="portal-username-input" /></div>
                    <div><Label className="text-xs">SGI Password *</Label><Input type="password" value={portalForm.password} onChange={e => setPortalForm(p => ({ ...p, password: e.target.value }))} className="h-8 text-sm mt-1" data-testid="portal-password-input" /></div>
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" size="sm" disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-portal-btn">{saving ? 'Saving...' : 'Save Portal'}</Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setShowAddForm(false)}>Cancel</Button>
                  </div>
                </form>
              )}

              {loading ? <p className="text-sm text-muted-foreground text-center py-8">Loading...</p> : portals.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-lg border">
                  <Globe size={48} className="mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground text-sm">No portals configured. Add your StarGems credentials to start importing.</p>
                </div>
              ) : portals.map(p => (
                <div key={p.id} className="bg-white rounded-lg border p-5 flex items-center justify-between" data-testid={`portal-card-${p.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center"><Globe size={22} className="text-teal-600" /></div>
                    <div>
                      <h3 className="font-semibold">{p.portal_name}</h3>
                      <p className="text-xs text-muted-foreground">{p.portal_url} &middot; User: {p.username}</p>
                      {p.last_import_at && <p className="text-[10px] text-muted-foreground mt-0.5">Last import: {new Date(p.last_import_at).toLocaleString()} &middot; {p.import_count} orders imported</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button onClick={() => handleFetchOrders(p.id)} disabled={fetching} className="bg-teal-600 hover:bg-teal-700" data-testid={`fetch-orders-${p.id}`}>
                      {fetching && activePortalId === p.id ? <Loader2 size={15} className="animate-spin mr-2" /> : <Download size={15} className="mr-2" />}
                      {fetching && activePortalId === p.id ? 'Fetching from SGI...' : 'Fetch Open Orders'}
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => { if (confirm('Delete?')) deletePortal(p.id).then(fetchPortals); }}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ===== ORDER LIST (multi-select) ===== */}
          {step === 'list' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-base font-semibold">Open Orders from SGI</h2>
                  <p className="text-xs text-muted-foreground">
                    {orderList.length} CAD orders from SGI. Already-imported orders are unchecked by default.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-teal-600">{selectedOrders.size} selected</span>
                  <Button onClick={handleScrapeDetails} disabled={scraping || selectedOrders.size === 0} className="bg-teal-600 hover:bg-teal-700" data-testid="scrape-selected-btn">
                    {scraping ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />}
                    {scraping ? 'Scraping Details...' : `Import ${selectedOrders.size} Selected`}
                  </Button>
                </div>
              </div>

              {/* Select all — operates on selectable (not-yet-imported) rows only */}
              <div className="flex items-center gap-2 px-1">
                <button onClick={selectAll} className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground">
                  {selectableOrders.length > 0 && selectedOrders.size >= selectableOrders.length
                    ? <CheckSquare size={14} className="text-teal-600" />
                    : <Square size={14} />}
                  Select All ({selectableOrders.length} new)
                </button>
              </div>

              <div className="bg-white rounded-lg border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
                      <th className="w-8 px-3 py-2"></th>
                      <th className="text-left px-3 py-2">Tab</th>
                      <th className="text-left px-3 py-2">Order #</th>
                      <th className="text-left px-3 py-2">Comp #</th>
                      <th className="text-left px-3 py-2">Notes</th>
                      <th className="text-left px-3 py-2">Order By</th>
                      <th className="text-left px-3 py-2">From</th>
                      <th className="text-center px-3 py-2">Days</th>
                      <th className="text-center px-3 py-2">Rush</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderList.map(o => {
                      const isImported = !!o.already_imported;
                      const rowBg = selectedOrders.has(o.order_no)
                        ? 'bg-teal-50'
                        : isImported ? 'bg-slate-50/60 opacity-60 hover:opacity-80' : 'hover:bg-slate-50';
                      const existing = o.existing_task || {};
                      return (
                        <tr key={o.order_no} className={`border-b border-border/30 cursor-pointer transition-colors ${rowBg}`} onClick={() => toggleOrder(o.order_no)} data-testid={`order-row-${o.order_no}`}>
                          <td className="px-3 py-2">
                            {selectedOrders.has(o.order_no) ? <CheckSquare size={14} className="text-teal-600" /> : <Square size={14} className="text-muted-foreground" />}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${o.tab === 'CAD Request' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{o.tab}</span>
                          </td>
                          <td className="px-3 py-2 font-mono font-bold">
                            <div className="flex items-center gap-1.5">
                              <span>{o.order_no}</span>
                              {isImported && (
                                <span
                                  className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-slate-200 text-slate-600"
                                  title={existing.internal_cad_no || existing.style_no
                                    ? `Already imported as ${existing.internal_cad_no || existing.style_no}`
                                    : 'Already imported into KS-Tracker'}
                                >
                                  IMPORTED{existing.internal_cad_no ? ` \u00b7 ${existing.internal_cad_no}` : ''}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 font-mono text-muted-foreground">{o.comp_no || '\u2014'}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground max-w-[300px] truncate">{o.notes || '\u2014'}</td>
                          <td className="px-3 py-2">{o.order_by}</td>
                          <td className="px-3 py-2 text-muted-foreground">{o.received_from}</td>
                          <td className="px-3 py-2 text-center">{o.days_since || '\u2014'}</td>
                          <td className="px-3 py-2 text-center">
                            {o.is_rush && <AlertTriangle size={14} className="text-red-500 mx-auto" />}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ===== DETAIL PREVIEW + REVIEW + EDIT ===== */}
          {step === 'details' && importSession && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-base font-semibold">Review & Import ({importSession.orders?.length} orders)</h2>
                  <p className="text-xs text-muted-foreground">Click any order to review and edit. Images downloaded from SGI. Select orders and confirm to create CAD tasks.</p>
                </div>
                <Button onClick={handleConfirmImport} disabled={confirming || selectedForImport.size === 0} className="bg-green-600 hover:bg-green-700" data-testid="confirm-import-btn">
                  {confirming ? <Loader2 size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />}
                  {confirming ? 'Creating Tasks...' : `Create ${selectedForImport.size} CAD Tasks`}
                </Button>
              </div>

              {/* Order navigation bar */}
              <div className="flex gap-1 flex-wrap bg-white rounded-lg border p-2">
                {importSession.orders?.map((order, idx) => {
                  const selected = selectedForImport.has(idx);
                  const isActive = editingOrderIdx === idx;
                  return (
                    <button key={idx} onClick={() => { setEditingOrderIdx(idx); setExpandedOrder(idx); }}
                      className={`px-3 py-1.5 rounded text-xs font-mono transition-all flex items-center gap-1.5 ${isActive ? 'bg-slate-900 text-white' : selected ? 'bg-teal-50 text-teal-700 border border-teal-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                      data-testid={`order-tab-${idx}`}>
                      <button onClick={(e) => { e.stopPropagation(); setSelectedForImport(prev => { const next = new Set(prev); if (next.has(idx)) next.delete(idx); else next.add(idx); return next; }); }}
                        className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center ${selected ? 'bg-teal-500 border-teal-500 text-white' : 'border-slate-400'}`}>
                        {selected && <Check size={8} />}
                      </button>
                      #{order.order_no}
                      {order.priority === 'urgent' && <AlertTriangle size={10} className="text-red-400" />}
                      {order.error && <X size={10} className="text-red-400" />}
                    </button>
                  );
                })}
              </div>

              {/* Active order detail view */}
              {editingOrderIdx !== null && importSession.orders?.[editingOrderIdx] && (() => {
                const order = importSession.orders[editingOrderIdx];
                const updateField = (field, value) => {
                  const updated = { ...importSession };
                  updated.orders = [...updated.orders];
                  updated.orders[editingOrderIdx] = { ...updated.orders[editingOrderIdx], [field]: value };
                  setImportSession(updated);
                };

                // Array CRUD helpers — mirror TaskSummaryView's stones/findings/metal_details editors.
                const updateArray = (field, next) => updateField(field, next);
                const addStone = () => {
                  const stones = [...(order.diamonds || []), { category: 'Accent Stone', stone_category: 'Natural', ownership: 'KS Goods', stone_shape: 'Round', stone_quality: '', quantity: 1, size_mm: '', weight_per_stone: 0, total_weight: 0, setting_type: 'Prong' }];
                  updateArray('diamonds', stones);
                };
                const updateStone = (idx, field, value) => {
                  const stones = [...(order.diamonds || [])];
                  stones[idx] = { ...stones[idx], [field]: value };
                  if (field === 'quantity' || field === 'weight_per_stone') {
                    stones[idx].total_weight = (stones[idx].quantity || 0) * (stones[idx].weight_per_stone || 0);
                  }
                  updateArray('diamonds', stones);
                };
                const removeStone = (idx) => updateArray('diamonds', (order.diamonds || []).filter((_, i) => i !== idx));

                const addOtherStone = () => {
                  const stones = [...(order.other_stones || []), { stone_type: '', stone_shape: 'Round', stone_quality: '', size_mm: '', weight_per_stone: 0, quantity: 1, total_weight: 0, setting_type: 'Prong' }];
                  updateArray('other_stones', stones);
                };
                const updateOtherStone = (idx, field, value) => {
                  const stones = [...(order.other_stones || [])];
                  stones[idx] = { ...stones[idx], [field]: value };
                  if (field === 'quantity' || field === 'weight_per_stone') {
                    stones[idx].total_weight = (stones[idx].quantity || 0) * (stones[idx].weight_per_stone || 0);
                  }
                  updateArray('other_stones', stones);
                };
                const removeOtherStone = (idx) => updateArray('other_stones', (order.other_stones || []).filter((_, i) => i !== idx));

                const pieceFindings = FINDINGS_BY_PIECE_TYPE[order.piece_type] || [];
                const addFinding = () => updateArray('findings', [...(order.findings || []), { name: '', custom_name: '', code: '', weight: 0, details: '' }]);
                const updateFinding = (idx, field, value) => {
                  const findings = [...(order.findings || [])];
                  findings[idx] = { ...findings[idx], [field]: value };
                  if (field === 'code') {
                    const match = pieceFindings.find(f => f.code === value);
                    if (match) {
                      findings[idx].name = match.name;
                      const karat = order.metal_karat || '14K';
                      findings[idx].weight = match.weights?.[karat] || 0;
                    }
                  }
                  updateArray('findings', findings);
                };
                const removeFinding = (idx) => updateArray('findings', (order.findings || []).filter((_, i) => i !== idx));

                const addAdditionalMetal = () => updateArray('metal_details', [...(order.metal_details || []), { metal_type: 'Gold', metal_colour: 'Yellow', metal_karat: '18K', weight: null }]);
                const updateAdditionalMetal = (idx, patch) => {
                  const details = [...(order.metal_details || [])];
                  details[idx] = { ...details[idx], ...patch };
                  updateArray('metal_details', details);
                };
                const removeAdditionalMetal = (idx) => updateArray('metal_details', (order.metal_details || []).filter((_, i) => i !== idx));

                if (order.error) {
                  return <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">Error scraping order #{order.order_no}: {order.error}</div>;
                }

                return (
                  <div className="bg-white rounded-lg border shadow-sm">
                    {/* Header */}
                    <div className="flex items-center justify-between px-5 py-3 border-b bg-slate-50">
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-bold text-base">Order #{order.order_no}</span>
                        {order.priority === 'urgent' && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded"><AlertTriangle size={10} /> RUSH</span>}
                        <span className="text-xs text-muted-foreground">by {order.order_by}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost" disabled={editingOrderIdx === 0} onClick={() => { setEditingOrderIdx(editingOrderIdx - 1); setExpandedOrder(editingOrderIdx - 1); }} className="h-7 text-xs">Prev</Button>
                        <span className="text-xs text-muted-foreground">{editingOrderIdx + 1} / {importSession.orders.length}</span>
                        <Button size="sm" variant="ghost" disabled={editingOrderIdx >= importSession.orders.length - 1} onClick={() => { setEditingOrderIdx(editingOrderIdx + 1); setExpandedOrder(editingOrderIdx + 1); }} className="h-7 text-xs">Next</Button>
                      </div>
                    </div>

                    <div className="p-5 space-y-5">
                      {/* Images */}
                      {order.images?.length > 0 && (
                        <div>
                          <h3 className="text-xs font-semibold text-muted-foreground mb-2">REFERENCE IMAGES ({order.images.length})</h3>
                          <div className="flex gap-3 flex-wrap">
                            {order.images.map((img, i) => {
                              const imgSrc = img.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img;
                              return (
                                <a key={i} href={imgSrc} target="_blank" rel="noreferrer" className="block">
                                  <img src={imgSrc} alt={`Ref ${i + 1}`} className="w-28 h-28 rounded-lg border-2 border-slate-200 object-cover hover:border-teal-400 hover:shadow-lg transition-all" onError={e => { e.target.style.display = 'none'; }} />
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* ===== IDENTIFICATION ===== */}
                      {(() => {
                        const metalType = (order.metal_type || 'gold').toLowerCase();
                        const karatOptions = METAL_KARATS[metalType] || METAL_KARATS.gold;
                        const colourOptions = METAL_COLOURS[metalType] || METAL_COLOURS.gold;
                        const TextField = ({ k, label, span, type = 'text', placeholder, step }) => (
                          <div className={span === 2 ? 'col-span-2' : ''}>
                            <Label className="text-[10px] text-muted-foreground">{label}</Label>
                            <Input type={type} step={step} value={order[k] || ''} onChange={e => updateField(k, e.target.value)}
                              className="h-7 text-xs mt-0.5" placeholder={placeholder || label}
                              data-testid={`edit-${k}-${editingOrderIdx}`} />
                          </div>
                        );
                        const SelectField = ({ k, label, options }) => (
                          <div>
                            <Label className="text-[10px] text-muted-foreground">{label}</Label>
                            <Select value={order[k] || ''} onValueChange={v => updateField(k, v)}>
                              <SelectTrigger className="h-7 text-xs mt-0.5" data-testid={`edit-${k}-${editingOrderIdx}`}>
                                <SelectValue placeholder={label} />
                              </SelectTrigger>
                              <SelectContent>
                                {options.map(opt => {
                                  const value = typeof opt === 'string' ? opt : opt.value;
                                  const optLabel = typeof opt === 'string' ? opt : opt.label;
                                  return <SelectItem key={value} value={value}>{optLabel}</SelectItem>;
                                })}
                              </SelectContent>
                            </Select>
                          </div>
                        );

                        return (
                          <>
                            <section>
                              <h3 className="text-xs font-semibold text-muted-foreground mb-2">IDENTIFICATION</h3>
                              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                <div>
                                  <Label className="text-[10px] text-muted-foreground">Style No</Label>
                                  <Input value={order.style_no || ''} onChange={e => updateField('style_no', e.target.value)}
                                    className="h-7 text-xs mt-0.5"
                                    placeholder="e.g. KSR-1234"
                                    data-testid={`edit-style_no-${editingOrderIdx}`} />
                                </div>
                                <TextField k="party_cad_no" label="Party No" placeholder={`SG-${order.order_no}`} />
                                <div>
                                  <Label className="text-[10px] text-muted-foreground">Client</Label>
                                  <ClientSelect value={order.client_name || ''} onChange={v => updateField('client_name', v)}
                                    className="h-7 text-xs mt-0.5 w-full" />
                                </div>
                                <div>
                                  <Label className="text-[10px] text-muted-foreground">KS Design No</Label>
                                  <Input value={order.ks_design_no || ''} disabled
                                    className="h-7 text-xs mt-0.5 w-full bg-muted cursor-not-allowed font-mono font-semibold" />
                                </div>
                                <TextField k="customer_design_no" label="Customer Design No (from SGI)"
                                  placeholder={order.comp_order_no || 'SGI design no'} />
                                <TextField k="product_name" label="Product Name" span={2} />
                                <TextField k="collection_name" label="Collection" />
                              </div>
                            </section>

                            <section>
                              <h3 className="text-xs font-semibold text-muted-foreground mb-2">BASIC</h3>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <SelectField k="piece_type" label="Piece Type" options={PIECE_TYPES} />
                                <TextField k="size" label="Size" />
                                <TextField k="quantity" label="Quantity" type="number" />
                                <SelectField k="priority" label="Priority" options={PRIORITY_LEVELS} />
                              </div>
                            </section>

                            <section>
                              <h3 className="text-xs font-semibold text-muted-foreground mb-2">METAL & WEIGHTS</h3>
                              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                <SelectField k="metal_type" label="Metal Type" options={METAL_TYPES} />
                                <SelectField k="metal_karat" label="Karat" options={karatOptions} />
                                <SelectField k="metal_colour" label="Colour" options={colourOptions} />
                                <TextField k="net_weight" label="Net Wt (g)" type="number" step="0.00001" />
                                <TextField k="gross_weight" label="Gross Wt (g)" type="number" step="0.00001" />
                              </div>
                              {/* Additional metals — mirror TaskSummaryView (cap at 2) */}
                              <div className="mt-2 space-y-1.5">
                                {(order.metal_details || []).map((metal, idx) => {
                                  const mt = (metal.metal_type || 'gold').toLowerCase();
                                  return (
                                    <div key={`add-metal-${idx}`} className="flex items-center gap-2">
                                      <Select value={mt} onValueChange={v => updateAdditionalMetal(idx, { metal_type: v.charAt(0).toUpperCase() + v.slice(1), metal_colour: (METAL_COLOURS[v] || ['Yellow'])[0], metal_karat: (METAL_KARATS[v] || ['14K'])[0] })}>
                                        <SelectTrigger className="h-7 text-xs w-[100px]"><SelectValue /></SelectTrigger>
                                        <SelectContent>{METAL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                                      </Select>
                                      <Select value={metal.metal_karat || '14K'} onValueChange={v => updateAdditionalMetal(idx, { metal_karat: v })}>
                                        <SelectTrigger className="h-7 text-xs w-[80px]"><SelectValue /></SelectTrigger>
                                        <SelectContent>{(METAL_KARATS[mt] || ['14K']).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                                      </Select>
                                      <Select value={metal.metal_colour || 'Yellow'} onValueChange={v => updateAdditionalMetal(idx, { metal_colour: v })}>
                                        <SelectTrigger className="h-7 text-xs w-[90px]"><SelectValue /></SelectTrigger>
                                        <SelectContent>{(METAL_COLOURS[mt] || ['Yellow']).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                      </Select>
                                      <Input className="h-7 text-xs w-[90px]" type="number" step="0.01" placeholder="Wt (g)" value={metal.weight ?? ''} onChange={e => updateAdditionalMetal(idx, { weight: parseFloat(e.target.value) || null })} />
                                      <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => removeAdditionalMetal(idx)}><Trash2 size={12} /></Button>
                                    </div>
                                  );
                                })}
                                {(order.metal_details || []).length < 2 && (
                                  <Button variant="outline" size="sm" onClick={addAdditionalMetal} className="h-6 text-xs px-2">
                                    <Plus size={12} className="mr-1" />Add Metal
                                  </Button>
                                )}
                              </div>
                            </section>

                            <section>
                              <h3 className="text-xs font-semibold text-muted-foreground mb-2">STONES (summary)</h3>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <TextField k="diamond_type" label="Diamond Type" placeholder="Lab Grown / Natural" />
                                <TextField k="diamond_quality_code" label="Quality Code" placeholder="VS-GH" />
                                <TextField k="total_diamond_pieces" label="Total Pieces" type="number" />
                                <TextField k="total_diamond_weight_ct" label="Total Wt (ct)" type="number" />
                              </div>
                            </section>

                            <section>
                              <h3 className="text-xs font-semibold text-muted-foreground mb-2">DIAMONDS / STONES (rows)</h3>
                              {(order.diamonds || []).length > 0 && (
                                <div className="overflow-x-auto mb-2">
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="border-b border-border">
                                        <th className="text-left py-1 px-1 font-medium text-muted-foreground">Stone</th>
                                        <th className="text-left py-1 px-1 font-medium text-muted-foreground">Ownership</th>
                                        <th className="text-left py-1 px-1 font-medium text-muted-foreground">Cut</th>
                                        <th className="text-left py-1 px-1 font-medium text-muted-foreground">Category</th>
                                        <th className="text-left py-1 px-1 font-medium text-muted-foreground">Quality</th>
                                        <th className="text-left py-1 px-1 font-medium text-muted-foreground">Size</th>
                                        <th className="text-left py-1 px-1 font-medium text-muted-foreground">Ct. Wt</th>
                                        <th className="text-left py-1 px-1 font-medium text-muted-foreground">Qty</th>
                                        <th className="text-left py-1 px-1 font-medium text-muted-foreground">Setting</th>
                                        <th className="text-left py-1 px-1 font-medium text-muted-foreground">Total</th>
                                        <th></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(order.diamonds || []).map((st, idx) => (
                                        <tr key={`diamond-${idx}`} className="border-b border-border/30">
                                          <td className="py-0.5 px-0.5">
                                            {['Natural', 'Lab', 'CVD', 'Colour Stone'].includes(st.stone_category) ? (
                                              <Select value={st.stone_category || 'Natural'} onValueChange={v => { if (v === '__other') updateStone(idx, 'stone_category', ''); else updateStone(idx, 'stone_category', v); }}>
                                                <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[80px]"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                  {['Natural', 'Lab', 'CVD', 'Colour Stone'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                                                  <SelectItem value="__other">Other...</SelectItem>
                                                </SelectContent>
                                              </Select>
                                            ) : (
                                              <Input className="h-6 text-[11px] p-1 w-[80px]" value={st.stone_category || ''} onChange={e => updateStone(idx, 'stone_category', e.target.value)} placeholder="Type..." />
                                            )}
                                          </td>
                                          <td className="py-0.5 px-0.5">
                                            {['KS Goods', 'Party Goods'].includes(st.ownership) ? (
                                              <Select value={st.ownership || 'KS Goods'} onValueChange={v => { if (v === '__other') updateStone(idx, 'ownership', ''); else updateStone(idx, 'ownership', v); }}>
                                                <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[80px]"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                  <SelectItem value="KS Goods">KS Goods</SelectItem>
                                                  <SelectItem value="Party Goods">Party Goods</SelectItem>
                                                  <SelectItem value="__other">Other...</SelectItem>
                                                </SelectContent>
                                              </Select>
                                            ) : (
                                              <Input className="h-6 text-[11px] p-1 w-[80px]" value={st.ownership || ''} onChange={e => updateStone(idx, 'ownership', e.target.value)} placeholder="Type..." />
                                            )}
                                          </td>
                                          <td className="py-0.5 px-0.5">
                                            {STONE_SHAPES.includes(st.stone_shape) ? (
                                              <Select value={st.stone_shape || 'Round'} onValueChange={v => { if (v === '__other') updateStone(idx, 'stone_shape', ''); else updateStone(idx, 'stone_shape', v); }}>
                                                <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[70px]"><SelectValue /></SelectTrigger>
                                                <SelectContent>{STONE_SHAPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}<SelectItem value="__other">Other...</SelectItem></SelectContent>
                                              </Select>
                                            ) : (
                                              <Input className="h-6 text-[11px] p-1 w-[70px]" value={st.stone_shape || ''} onChange={e => updateStone(idx, 'stone_shape', e.target.value)} placeholder="Cut..." />
                                            )}
                                          </td>
                                          <td className="py-0.5 px-0.5">
                                            <Select value={st.category || 'Accent Stone'} onValueChange={v => updateStone(idx, 'category', v)}>
                                              <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[80px]"><SelectValue /></SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="Center Stone">Center</SelectItem>
                                                <SelectItem value="Accent Stone">Accent</SelectItem>
                                                <SelectItem value="Side Stone">Side</SelectItem>
                                              </SelectContent>
                                            </Select>
                                          </td>
                                          <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-14" value={st.stone_quality || ''} onChange={e => updateStone(idx, 'stone_quality', e.target.value)} placeholder="VS1" /></td>
                                          <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-14" value={st.size_mm || ''} onChange={e => updateStone(idx, 'size_mm', e.target.value)} placeholder="mm" /></td>
                                          <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-16" type="number" step="0.00001" value={st.weight_per_stone || ''} onChange={e => updateStone(idx, 'weight_per_stone', parseFloat(e.target.value) || 0)} placeholder="ct" /></td>
                                          <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-12" type="number" value={st.quantity || ''} onChange={e => updateStone(idx, 'quantity', parseInt(e.target.value) || 0)} /></td>
                                          <td className="py-0.5 px-0.5">
                                            <Select value={st.setting_type || 'Prong'} onValueChange={v => updateStone(idx, 'setting_type', v)}>
                                              <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[90px]"><SelectValue /></SelectTrigger>
                                              <SelectContent>{SETTING_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                            </Select>
                                          </td>
                                          <td className="py-0.5 px-0.5 text-center text-[11px] text-muted-foreground">{(st.total_weight || 0).toFixed(2)}</td>
                                          <td><Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-destructive" onClick={() => removeStone(idx)}><Trash2 size={10} /></Button></td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              <Button variant="outline" size="sm" onClick={addStone} className="h-7 text-xs"><Plus size={12} className="mr-1" />Add Stone</Button>
                            </section>

                            <section>
                              <h3 className="text-xs font-semibold text-muted-foreground mb-2">OTHER STONES (colour / non-diamond)</h3>
                              {(order.other_stones || []).length > 0 && (
                                <div className="overflow-x-auto mb-2">
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="border-b border-border">
                                        <th className="text-left py-1 px-1 font-medium text-muted-foreground">Type</th>
                                        <th className="text-left py-1 px-1 font-medium text-muted-foreground">Cut</th>
                                        <th className="text-left py-1 px-1 font-medium text-muted-foreground">Quality</th>
                                        <th className="text-left py-1 px-1 font-medium text-muted-foreground">Size</th>
                                        <th className="text-left py-1 px-1 font-medium text-muted-foreground">Ct. Wt</th>
                                        <th className="text-left py-1 px-1 font-medium text-muted-foreground">Qty</th>
                                        <th className="text-left py-1 px-1 font-medium text-muted-foreground">Setting</th>
                                        <th></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(order.other_stones || []).map((st, idx) => (
                                        <tr key={`other-${idx}`} className="border-b border-border/30">
                                          <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-[90px]" value={st.stone_type || ''} onChange={e => updateOtherStone(idx, 'stone_type', e.target.value)} placeholder="Ruby..." /></td>
                                          <td className="py-0.5 px-0.5">
                                            <Select value={STONE_SHAPES.includes(st.stone_shape) ? st.stone_shape : 'Round'} onValueChange={v => updateOtherStone(idx, 'stone_shape', v)}>
                                              <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[70px]"><SelectValue /></SelectTrigger>
                                              <SelectContent>{STONE_SHAPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                            </Select>
                                          </td>
                                          <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-14" value={st.stone_quality || ''} onChange={e => updateOtherStone(idx, 'stone_quality', e.target.value)} placeholder="AA" /></td>
                                          <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-14" value={st.size_mm || ''} onChange={e => updateOtherStone(idx, 'size_mm', e.target.value)} placeholder="mm" /></td>
                                          <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-16" type="number" step="0.00001" value={st.weight_per_stone || ''} onChange={e => updateOtherStone(idx, 'weight_per_stone', parseFloat(e.target.value) || 0)} placeholder="ct" /></td>
                                          <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-12" type="number" value={st.quantity || ''} onChange={e => updateOtherStone(idx, 'quantity', parseInt(e.target.value) || 0)} /></td>
                                          <td className="py-0.5 px-0.5">
                                            <Select value={st.setting_type || 'Prong'} onValueChange={v => updateOtherStone(idx, 'setting_type', v)}>
                                              <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[90px]"><SelectValue /></SelectTrigger>
                                              <SelectContent>{SETTING_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                            </Select>
                                          </td>
                                          <td><Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-destructive" onClick={() => removeOtherStone(idx)}><Trash2 size={10} /></Button></td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              <Button variant="outline" size="sm" onClick={addOtherStone} className="h-7 text-xs"><Plus size={12} className="mr-1" />Add Other Stone</Button>
                            </section>

                            <section>
                              <h3 className="text-xs font-semibold text-muted-foreground mb-2">FINDINGS / COMPONENTS</h3>
                              <div className="space-y-1.5 mb-2">
                                {(order.findings || []).map((f, idx) => (
                                  <div key={`finding-${idx}`} className="flex items-center gap-2">
                                    <Select value={f.code || ''} onValueChange={v => updateFinding(idx, 'code', v)}>
                                      <SelectTrigger className="h-7 text-xs flex-1"><SelectValue placeholder="Select finding..." /></SelectTrigger>
                                      <SelectContent>{pieceFindings.filter(pf => pf.code !== 'NONE').map(pf => <SelectItem key={pf.code} value={pf.code}>{pf.name}</SelectItem>)}</SelectContent>
                                    </Select>
                                    <Input className="h-7 text-xs w-24" value={f.custom_name || ''} onChange={e => updateFinding(idx, 'custom_name', e.target.value)} placeholder="Name" />
                                    <Input className="h-7 text-xs w-20" value={f.weight ?? ''} onChange={e => updateFinding(idx, 'weight', e.target.value)} placeholder="Weight" />
                                    <Input className="h-7 text-xs w-28" value={f.details || ''} onChange={e => updateFinding(idx, 'details', e.target.value)} placeholder="Details" />
                                    <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => removeFinding(idx)}><Trash2 size={12} /></Button>
                                  </div>
                                ))}
                              </div>
                              <Button variant="outline" size="sm" onClick={addFinding} className="h-7 text-xs"><Plus size={12} className="mr-1" />Add Finding</Button>
                            </section>

                            <section>
                              <h3 className="text-xs font-semibold text-muted-foreground mb-2">FINISHING & ENGRAVING</h3>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <SelectField k="finishing" label="Finishing" options={FINISHING_TYPES} />
                                <TextField k="texture" label="Texture" />
                                <TextField k="rhodium_plating" label="Rhodium Plating" />
                                <TextField k="engraving" label="Engraving" />
                              </div>
                            </section>

                            <section>
                              <h3 className="text-xs font-semibold text-muted-foreground mb-2">CAD &amp; DATES</h3>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <TextField k="cad_file_name" label="CAD File Name" />
                                <TextField k="cad_url" label="CAD Link / URL" placeholder="https://..." />
                                <div>
                                  <Label className="text-[10px] text-muted-foreground">CAD Due Date <span className="text-red-500">*</span></Label>
                                  <Input
                                    type="date"
                                    value={order.target_date || order.deadline || ''}
                                    onChange={e => {
                                      // Write to both fields so CAD summary edit (which reads
                                      // `deadline`) and the deadline-filter sidebar (which reads
                                      // `target_date`) stay aligned. Same value, two keys.
                                      const v = e.target.value;
                                      updateField('target_date', v);
                                      updateField('deadline', v);
                                    }}
                                    className={`h-7 text-xs mt-0.5 ${!(order.target_date || order.deadline) ? 'border-amber-300 bg-amber-50' : ''}`}
                                    data-testid={`edit-cad-due-date-${editingOrderIdx}`}
                                  />
                                </div>
                              </div>
                            </section>

                            <section>
                              <h3 className="text-xs font-semibold text-muted-foreground mb-2">WORKFLOW</h3>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div>
                                  <Label className="text-[10px] text-muted-foreground">Designer</Label>
                                  <Select
                                    value={order.designer_id || ''}
                                    onValueChange={v => {
                                      const found = executors.find(ex => ex.id === v);
                                      updateField('designer_id', v);
                                      updateField('designer_name', found ? found.name : '');
                                    }}
                                  >
                                    <SelectTrigger className="h-7 text-xs mt-0.5" data-testid={`edit-designer_id-${editingOrderIdx}`}>
                                      <SelectValue placeholder="Select designer..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {executors.map(ex => (
                                        <SelectItem key={ex.id} value={ex.id}>{ex.name}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <SelectField k="cad_difficulty" label="CAD Difficulty" options={[
                                  { value: 'easy', label: 'Easy' },
                                  { value: 'medium', label: 'Medium' },
                                  { value: 'hard', label: 'Hard' },
                                  { value: 'very_hard', label: 'Very Hard' },
                                ]} />
                                <TextField k="cad_points" label="CAD Points (hrs)" type="number" step="0.25" />
                              </div>
                            </section>
                          </>
                        );
                      })()}

                      {/* Per-image notes table from SGI — shown when the scraper captured
                          the structured Image · Notes · Date layout. Lets the user verify
                          the import will preserve the SGI side-by-side notes BEFORE
                          confirming. When this table is showing, the freeform Notes
                          textarea below is hidden so the same content isn't repeated
                          in two places. */}
                      {(order.image_notes?.length > 0) && (
                        <section>
                          <Label className="text-[10px] text-muted-foreground mb-1 block">
                            Images Uploaded ({order.image_notes.length}) — per-image notes from SGI
                          </Label>
                          <ImagesUploadedTable entries={order.image_notes} />
                        </section>
                      )}

                      {/* Notes & Instructions — Notes textarea hidden when the
                          structured image_notes table above covers it. Special
                          Instructions / Stamping Instructions are always shown
                          because they're separate fields on SGI. */}
                      <section className="space-y-3">
                        {!(order.image_notes?.length > 0) && (
                          <div>
                            <Label className="text-[10px] text-muted-foreground">Notes</Label>
                            <textarea value={order.notes || ''} onChange={e => updateField('notes', e.target.value)}
                              className="w-full mt-0.5 text-xs p-2 border rounded-md bg-slate-50 min-h-[60px] resize-y"
                              data-testid={`edit-notes-${editingOrderIdx}`} />
                          </div>
                        )}
                        <div>
                          <Label className="text-[10px] text-muted-foreground">Special Instructions</Label>
                          <textarea value={order.special_instructions || ''} onChange={e => updateField('special_instructions', e.target.value)}
                            className="w-full mt-0.5 text-xs p-2 border rounded-md bg-slate-50 min-h-[44px] resize-y"
                            data-testid={`edit-special_instructions-${editingOrderIdx}`} />
                        </div>
                        <div>
                          <Label className="text-[10px] text-muted-foreground">Stamping Instructions</Label>
                          <textarea value={order.stamping_instructions || ''} onChange={e => updateField('stamping_instructions', e.target.value)}
                            className="w-full mt-0.5 text-xs p-2 border rounded-md bg-slate-50 min-h-[44px] resize-y"
                            data-testid={`edit-stamping_instructions-${editingOrderIdx}`} />
                        </div>
                        <div>
                          <Label className="text-[10px] text-muted-foreground">Other Details</Label>
                          <textarea value={order.other_details || ''} onChange={e => updateField('other_details', e.target.value)}
                            className="w-full mt-0.5 text-xs p-2 border rounded-md bg-slate-50 min-h-[44px] resize-y"
                            data-testid={`edit-other_details-${editingOrderIdx}`} />
                        </div>
                      </section>

                      {/* Specs from portal */}
                      {order.specs_table && Object.keys(order.specs_table).length > 0 && (
                        <div>
                          <h3 className="text-xs font-semibold text-muted-foreground mb-1">PORTAL SPECS (raw)</h3>
                          <div className="grid grid-cols-3 gap-2 text-xs bg-slate-50 rounded p-2">
                            {Object.entries(order.specs_table).map(([k, v]) => (
                              <div key={k}><span className="text-muted-foreground">{k}:</span> <span className="font-medium">{v}</span></div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

        </main>
      </div>

      <StyleNoPromptDialog
        open={styleNoPromptOpen}
        items={styleNoPromptItems}
        title={`Style No required for ${styleNoPromptItems.length} order${styleNoPromptItems.length === 1 ? '' : 's'}`}
        onSave={handleStyleNoPromptSave}
        onCancel={() => { setStyleNoPromptOpen(false); setStyleNoPromptItems([]); }}
      />
      <StyleNoPromptDialog
        open={dueDatePromptOpen}
        items={dueDatePromptItems}
        title={`CAD Due Date required for ${dueDatePromptItems.length} order${dueDatePromptItems.length === 1 ? '' : 's'}`}
        description="Set a CAD due date for each order below. The same value is stored on both the deadline and target_date fields so it shows up consistently across CAD summary, filters and exports."
        inputType="date"
        inputPlaceholder=""
        saveLabel="Save Dates & Continue"
        onSave={handleDueDatePromptSave}
        onCancel={() => { setDueDatePromptOpen(false); setDueDatePromptItems([]); }}
      />
    </CollapsibleLayout>
  );
}
